
import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Shirt, Image, Music, Package } from "lucide-react";

interface DesignServiceFormProps {
  designDetails: string;
  setDesignDetails: (value: string) => void;
  additionalRequests: string;
  setAdditionalRequests: (value: string) => void;
  selectedPackage: string;
  setSelectedPackage: (value: string) => void;
  tshirtPrinting: boolean;
  setTshirtPrinting: (value: boolean) => void;
  photoBooth: boolean;
  setPhotoBooth: (value: boolean) => void;
  music: boolean;
  setMusic: (value: boolean) => void;
  posters: boolean;
  setPosters: (value: boolean) => void;
  printQuantity: number;
}

const DesignServiceForm: React.FC<DesignServiceFormProps> = ({
  designDetails,
  setDesignDetails,
  additionalRequests,
  setAdditionalRequests,
  selectedPackage,
  setSelectedPackage,
  tshirtPrinting,
  setTshirtPrinting,
  photoBooth,
  setPhotoBooth,
  music,
  setMusic,
  posters,
  setPosters,
  printQuantity,
}) => {
  // Update service options when package changes, but only as defaults
  useEffect(() => {
    switch (selectedPackage) {
      case "standard":
        setTshirtPrinting(true);
        setPhotoBooth(false);
        setMusic(false);
        setPosters(false);
        break;
      case "premium":
        setTshirtPrinting(true);
        setPhotoBooth(true);
        setMusic(false);
        setPosters(true);
        break;
      case "deluxe":
        setTshirtPrinting(true);
        setPhotoBooth(true);
        setMusic(true);
        setPosters(true);
        break;
      default:
        break;
    }
  }, [selectedPackage, setTshirtPrinting, setPhotoBooth, setMusic, setPosters]);

  const handlePackageChange = (value: string) => {
    setSelectedPackage(value);
    // The useEffect will handle updating the service options
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="design-details">Design Details</Label>
        <Textarea
          id="design-details"
          placeholder="Describe your desired design, colors, themes, etc."
          className="resize-none h-24"
          value={designDetails}
          onChange={(e) => setDesignDetails(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="additional-requests">Additional Requests</Label>
        <Textarea
          id="additional-requests"
          placeholder="Any other information or requests?"
          className="resize-none h-20"
          value={additionalRequests}
          onChange={(e) => setAdditionalRequests(e.target.value)}
        />
      </div>
      
      <Separator className="my-4" />
      
      <div>
        <Label className="text-base font-medium">Select a Package</Label>
        <RadioGroup 
          defaultValue="standard"
          value={selectedPackage} 
          onValueChange={handlePackageChange}
          className="mt-2"
        >
          <div className="flex items-center space-x-2 border p-3 rounded-md hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="standard" id="standard-package" />
            <Label htmlFor="standard-package" className="cursor-pointer flex flex-col">
              <span className="font-medium">Standard Package ($99.99)</span>
              <span className="text-sm text-gray-500">T-shirt printing only</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2 border p-3 rounded-md hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="premium" id="premium-package" />
            <Label htmlFor="premium-package" className="cursor-pointer flex flex-col">
              <span className="font-medium">Premium Package ($199.99)</span>
              <span className="text-sm text-gray-500">T-shirt printing, photo booth, and posters</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2 border p-3 rounded-md hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="deluxe" id="deluxe-package" />
            <Label htmlFor="deluxe-package" className="cursor-pointer flex flex-col">
              <span className="font-medium">Deluxe Package ($299.99)</span>
              <span className="text-sm text-gray-500">All services included</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Separator className="my-4" />

      <div>
        <h3 className="text-lg font-semibold mb-3">Party Services</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select the services you'd like for your event:
        </p>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded text-madts-purple focus:ring-madts-purple"
              checked={tshirtPrinting}
              onChange={(e) => setTshirtPrinting(e.target.checked)}
            />
            <span className="flex items-center gap-2">
              <Shirt className="h-4 w-4 text-gray-500" />
              T-Shirt Printing ($15/shirt)
              {tshirtPrinting && printQuantity >= 4 && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  Buy 3 Get the 4th Free!
                </span>
              )}
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded text-madts-purple focus:ring-madts-purple"
              checked={photoBooth}
              onChange={(e) => setPhotoBooth(e.target.checked)}
            />
            <span className="flex items-center gap-2">
              <Image className="h-4 w-4 text-gray-500" />
              Photo Booth ($150)
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded text-madts-purple focus:ring-madts-purple"
              checked={music}
              onChange={(e) => setMusic(e.target.checked)}
            />
            <span className="flex items-center gap-2">
              <Music className="h-4 w-4 text-gray-500" />
              Music & DJ Services ($200)
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded text-madts-purple focus:ring-madts-purple"
              checked={posters}
              onChange={(e) => setPosters(e.target.checked)}
            />
            <span className="flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-500" />
              Poster Prints ($10/poster)
            </span>
          </label>
        </div>
      </div>
      
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-500">Coming Soon:</p>
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
            Color T-Shirts
          </span>
          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
            Mugs
          </span>
          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
            Hoodies
          </span>
        </div>
      </div>
    </div>
  );
};

export default DesignServiceForm;
